import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {FlexBox} from '@brg/ui';
import {
    CustomDeleteIcon,
    CustomEditIcon,
    DateNote,
    DateNoteDay,
    DateNoteMonth,
    DateNoteWeekday,
    DescriptionBlockNote,
    DescriptionNote,
    DescriptionNoteDeleteButton,
    DescriptionNoteDetails,
    DescriptionNoteEditButton,
    DescriptionNoteGroupButtons,
    DescriptionNoteLink,
    DescriptionNoteText,
    DescriptionNoteTheme,
    DescriptionNoteTime,
    InputSectionFlexBox,
    LMSScheduleEditPageGridInput,
    ScheduleButtonWrapper,
    ScheduleEditPageGridLabel,
    ScheduleModalAddButton,
    ScheduleModalBody,
    ScheduleModalCancelButton,
    ScheduleModalContent,
    ScheduleModalForm,
    ScheduleModalInput,
    ScheduleModalLessonsContent,
    ScheduleModalNote,
    ScheduleModalNotes,
    ScheduleModalPlanner,
    ScheduleModalTimeInput,
    ScheduleModalTitle,
    ScheduleModalWrapper,
    ScheduleModalСalendar
} from './styles';
import {InputSection} from '@brg/controls';
import {Input} from "@brg/input";
import {CalendarComponent} from './CalendarComponent/CalendarComponent';
import {ILesson, IThemeItem, LMSLessonType} from "../../constants/ILMS";
import {useTeamGroups} from "../../hooks/useTeamGroups";
import {useGetThemeLessons} from "../../hooks/api/LMS/useGetThemeLessons";
import {API, Endpoints, Group} from "@brg/api";
import {useTranslation} from "react-i18next";
import {makeBilingualLangObject} from "../../helpers/changeLangObject";
import {DottedIcon} from "../../icons/DottedIcon";
import SortableList, {SortableItem} from "react-easy-sort";
import { arrayMoveImmutable } from 'shared/utils/arrayMove';

const fixTimezoneOffsetToShow = (date: Date | undefined) : Date => {
    if(!date) return new Date();
    return new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
}

const fixTimezoneOffsetToSave = (date: Date | undefined) : Date => {
    if(!date) return new Date();
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
}

export interface ScheduleModalProps {
    themes: IThemeItem<'Local'>[];
    currentTeamId?: string;
    projectId?: string;
    themeId?: string;
}

export const LESSON_OPTIONS = [
    {label: "Видео", value: LMSLessonType.Video},
    {label: "По дате", value: LMSLessonType.WithDate},
    {label: "Без даты", value: LMSLessonType.WithoutDate},
]

export const ScheduleModal: FC<ScheduleModalProps> = (props) => {

    const {
        themeId,
        currentTeamId,
        projectId,
        themes
    } = props;

    const [t, setT] = useState(Date.now())
    const [tLessons, setTLessons] = useState(Date.now())
    const groups = useTeamGroups(currentTeamId || "", t)
    const { i18n } = useTranslation()
    const translatedGroups = useMemo(() => API.toLang(groups, i18n.language) as Group<'Local'>[], [i18n.language, groups])
    const [activeTheme, setActiveTheme] = useState<null | {value: string, label: string}>(null)
    const {lessons: themeLessons} = useGetThemeLessons(projectId, activeTheme?.value, tLessons)
    const [lessons, setLessons] = useState<ILesson<'Global'>[]>([]);

    useEffect(() => {
        const grouppedLessons = [...themeLessons].map((i) => {
            return {
                ...i,
            }
        })
        setLessons(grouppedLessons)
    }, [themeLessons])

    const themeOptions = useMemo(() => themes?.map((i) => ({value: i?.id || "", label: i?.name || ""})) || [], [themes])
    const [editableLesson, setEditableLesson] = useState<null | string>(null)
    const [startTime, setStartTime] = useState('00:00')
    const [endTime, setEndTime] = useState('00:00')
    const [link, setLink] = useState('')
    const [name, setName] = useState([{lang: 'ru', name: ''}, {lang: 'en', name: ''}])
    const translatedName = useMemo(() => (API.toLang({val: name}, i18n.language) as any)?.val as string, [name, i18n])
    const [lessonType, setLessonType] = useState<{value: LMSLessonType, label: string}>(LESSON_OPTIONS[1])

    useEffect(() => {
        setActiveTheme(themeOptions?.find(i => i?.value === themeId) || null)
    }, [themeOptions])

    // @ts-ignore
    const groupOptions = useMemo(() => translatedGroups?.map((i) => ({value: i?.id || "", label: i?.title || ""})) || [], [translatedGroups])
    const [activeGroups, setActiveGroups] = useState<{value: string, label: string}[]>([])
    const [newLessonDate, setNewLessonDate] = useState<Date | undefined>(new Date())
    const eventsDate = useMemo(() => {
        if (!themeLessons || themeLessons?.length === 0) return [];
        return themeLessons?.map((lesson: ILesson) => fixTimezoneOffsetToShow(new Date(lesson.start)));
    }, [themeLessons])

    // @ts-ignore
    const onChangeTime = useCallback((value, setValue) => {
        const values = value.split(':');

        if (values?.length < 2) return;

        setValue(`${values[0] > 23 ? 23 : values[0]}:${values[1] > 59 ? 59 : values[1]}`)
    }, [])

    const isConfirmButtonActive = useMemo(() => {

        const firstTimeArr = startTime?.split(':')
        const firstTime = Number(firstTimeArr[0]) * 60 + Number(firstTimeArr[1])
        const secondTimeArr = endTime?.split(':')
        const secondTime = Number(secondTimeArr[0]) * 60 + Number(secondTimeArr[1])

        if (lessonType?.value === LMSLessonType.WithDate) return link?.length > 0 && newLessonDate && activeTheme && activeGroups?.length > 0 && firstTime < secondTime
        if (lessonType?.value === LMSLessonType.Video) return link?.length > 0 && activeGroups?.length > 0
        return activeGroups?.length > 0

    }, [link, newLessonDate, activeTheme, activeGroups, startTime, endTime, lessonType])

    const getLessonBlockData = useCallback((lesson: ILesson) => {

        const startDate = new Date(lesson?.start);
        const endDate = new Date(lesson?.end);
        const pad = (n: any) => n < 10 ? '0' + n : n;

        const options = { weekday: 'short', day: 'numeric', month: 'long' };
        // @ts-ignore
        const formattedDate = startDate.toLocaleDateString('ru-RU', options)?.split(' ');
        const startHours = pad(startDate.getHours());
        const startMinutes = pad(startDate.getMinutes());
        const endHours = pad(endDate.getHours());
        const endMinutes = pad(endDate.getMinutes());
        const neededGroups = translatedGroups?.filter(i => lesson?.groupIds?.includes(i?.id));
        const groupsName = neededGroups
            // @ts-ignore
            ?.reduce((acc, curr, currentIndex) => (`${acc} ${curr?.title}${currentIndex + 1 === neededGroups?.length ? '' : ','}`), '')

        // @ts-ignore
        const title = API.toLang({val: lesson?.title}, 'ru')?.val?.length > 0 ? API.toLang({val: lesson?.title}, 'ru')?.val : ''

        return {
            themeName: title,
            date: {
                dayWeek: (formattedDate[0] || '')?.toUpperCase()?.slice(0, -1),
                dayNumber: formattedDate[1] || '',
                month: formattedDate[2] || '',
            },
            groupsName: groupsName,
            time: `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`
        }
    }, [themes, translatedGroups])

    const onDelete = useCallback((id: string) => {

        const confirmation = window.confirm('Вы действительно хотите удалить занятие?');

        if (!confirmation) return;

        if (editableLesson === id) setEditableLesson(null);

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(`/api/LMS/DeleteThemeLesson`)
            .post()
            .body({
                id,
                themeId: activeTheme?.value,
                moduleProjectId: projectId
            })
            .release()
            .then((r) => {
                setTLessons(Date.now())
            })
    }, [activeTheme, projectId, editableLesson])

    const onReset = useCallback(() => {
        setStartTime('00:00')
        setEndTime('00:00')
        setLink('')
        setActiveGroups([])
        setName(makeBilingualLangObject(''))
        setLessonType(LESSON_OPTIONS[1])
    }, [])

    const onStartEdit = useCallback((id: string) => {

        if (editableLesson === id) {
            setEditableLesson(null)
            onReset()
            return;
        }
        const lesson = lessons?.find(i => i?.id === id);

        if (!lesson) return;

        const startDate = new Date(lesson?.start);
        const endDate = new Date(lesson?.end);
        const pad = (n: any) => n < 10 ? '0' + n : n;

        const startHours = pad(startDate.getHours());
        const startMinutes = pad(startDate.getMinutes());
        const endHours = pad(endDate.getHours());
        const endMinutes = pad(endDate.getMinutes());

        startDate.setHours(0)
        startDate.setMinutes(0)

        setStartTime(`${startHours}:${startMinutes}`)
        setEndTime(`${endHours}:${endMinutes}`)
        setLink(lesson?.lessonLink)
        setName(lesson?.title)
        setLessonType(LESSON_OPTIONS[lesson?.type === undefined ? 1 : lesson?.type])
        setNewLessonDate(startDate)
        setActiveGroups(groupOptions.filter(i => lesson?.groupIds?.includes(i?.value)))

        setEditableLesson(id)
    }, [lessons, groupOptions, editableLesson])

    const onCancel = useCallback(() => {
        setEditableLesson(null)
        onReset()
    }, [])

    const onSort = useCallback((oldIndex: number, newIndex: number) => {
        const arr = arrayMoveImmutable(lessons, oldIndex, newIndex)
        setLessons(arr)

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(`/api/LMS/UpdateThemeLessonPositions`)
            .post()
            .body(arr?.map(i => i?.id || "") || [])
            .release()
            .then((r) => r.json)

    }, [lessons])

    const onConfirm = useCallback(() => {

        const firstTimeArr = startTime?.split(':')
        const secondTimeArr = endTime?.split(':')

        const startDate = fixTimezoneOffsetToSave(new Date(newLessonDate || Date.now()))
        startDate.setHours(Number(firstTimeArr[0]));
        startDate.setMinutes(Number(firstTimeArr[1]));
        const endDate = fixTimezoneOffsetToSave(new Date(newLessonDate || Date.now()))
        endDate.setHours(Number(secondTimeArr[0]));
        endDate.setMinutes(Number(secondTimeArr[1]));

        const lesson = lessons?.find(i => i?.id === editableLesson);
        const firstBody = editableLesson ? lesson : {}

        const body = {
            ...firstBody,
            moduleProjectId: projectId,
            themeId: activeTheme?.value,
            start: startDate,
            end: endDate,
            groupIds: activeGroups?.map(i => i?.value) || [],
            lessonLink: link,
            lessonLinkText: [{lang: "ru", name: ""},{lang: "en", name: ""}],
            title: name,
            type: lessonType?.value,
        }

        onReset();
        setEditableLesson(null)

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(editableLesson ? `/api/LMS/UpdateThemeLesson` : `/api/LMS/AddThemeLesson`)
            .post()
            .body(body)
            .release()
            .then((r) => r.json)
            .then((r) => {
                setTLessons(Date.now())
            })

    }, [projectId, activeTheme, activeGroups, link, startTime, endTime, newLessonDate, editableLesson, lessons, name, lessonType])

    return (
        <ScheduleModalBody offsetTop={70} width={'100%'} justify='center' align='center' justify-content='center'>
            <ScheduleModalWrapper>
                <ScheduleModalTitle>Расписание</ScheduleModalTitle>
                <ScheduleModalContent>
                    <ScheduleModalForm>
                        <InputSection>
                            <InputSectionFlexBox>
                            <FlexBox direction="column" directionMobile="column" width="100%">
                                <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                        {'Тема'}
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Dropdown
                                            value={activeTheme}
                                            onChange={setActiveTheme}
                                            options={themeOptions}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>

                                <FlexBox width="100%" justify="space-between">
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                            {'Тип урока'}
                                        </ScheduleEditPageGridLabel>
                                        <LMSScheduleEditPageGridInput>
                                            <Input.Dropdown
                                                value={lessonType}
                                                onChange={setLessonType}
                                                options={LESSON_OPTIONS}
                                            />
                                        </LMSScheduleEditPageGridInput>
                                    </ScheduleModalInput>
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }} gx={8}>
                                            <span>{'Название'}</span>
                                        </ScheduleEditPageGridLabel>
                                        <LMSScheduleEditPageGridInput>
                                            <Input.Text
                                                className={'text-input'}
                                                placeholder={'Введите название урока'}
                                                value={translatedName}
                                                onChange={(e: any) => setName(makeBilingualLangObject(e.target.value))}
                                            />
                                        </LMSScheduleEditPageGridInput>
                                    </ScheduleModalInput>
                                </FlexBox>

                                {lessonType?.value == LMSLessonType.WithDate && <>
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel>{'Начало занятия'}</ScheduleEditPageGridLabel>
                                        <ScheduleModalTimeInput directionMobile={"column"} gyMobile={8}>
                                            <input className={'time-input'} type={'time'} value={startTime} onChange={(e: any) => onChangeTime(e.target.value, setStartTime)} />
                                        </ScheduleModalTimeInput>
                                    </ScheduleModalInput>

                                    <ScheduleModalInput>
                                        <ScheduleModalTimeInput directionMobile={"column"} gyMobile={8}>
                                            <ScheduleEditPageGridLabel>{'Окончание занятия'}</ScheduleEditPageGridLabel>
                                            <input className={'time-input'} type={'time'} value={endTime} onChange={(e: any) => onChangeTime(e.target.value, setEndTime)} />
                                        </ScheduleModalTimeInput>
                                    </ScheduleModalInput>
                                </>}

                            </FlexBox>
                            <FlexBox direction="column" directionMobile="column" width="100%">
                                {lessonType?.value !== LMSLessonType.WithoutDate && <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }} gx={8}>
                                        <span>{lessonType?.value == LMSLessonType.Video ? 'Ссылка на видео' : 'Ссылка на занятие'}</span>
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Text
                                            className={'text-input'}
                                            placeholder={lessonType?.value == LMSLessonType.Video ? 'Вставьте ссылку на видео' : 'Вставьте ссылку на занятие'}
                                            value={link}
                                            onChange={(e: any) => setLink(e.target.value)}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>}

                                <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                        {'Группа'}
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Dropdown
                                            isMulti
                                            value={activeGroups}
                                            onChange={setActiveGroups}
                                            options={groupOptions}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>
                            </FlexBox>
                            </InputSectionFlexBox>
                        </InputSection>
                        <ScheduleButtonWrapper width="100%" direction="column" directionMobile="column">
                            <ScheduleModalAddButton
                                className="add-button"
                                disabled={!isConfirmButtonActive}
                                onClick={onConfirm}
                            >
                                {editableLesson ? 'Сохранить изменения' : 'Добавить занятие'}
                            </ScheduleModalAddButton>
                            {editableLesson && <ScheduleModalCancelButton
                                className="add-button"
                                onClick={onCancel}
                            >
                                {'Отменить'}
                            </ScheduleModalCancelButton>}
                        </ScheduleButtonWrapper>
                    </ScheduleModalForm>
                    <ScheduleModalLessonsContent>
                    <ScheduleModalPlanner>
                        <ScheduleModalNotes>
                            <ScheduleModalNote>
                                <SortableList onSortEnd={onSort}>
                                <DescriptionBlockNote>
                                    {lessons?.map(lesson => {

                                        const lessonBlockData = getLessonBlockData(lesson);

                                        return <SortableItem key={"team-tab-sortable_" + lesson?.id}><DescriptionNote key={lesson?.id}>
                                            <FlexBox width={24}>
                                                <DottedIcon/>
                                            </FlexBox>
                                            {lesson?.type == LMSLessonType.WithDate && <DateNote isFirst={true}>
                                                <DateNoteWeekday>{lessonBlockData?.date?.dayWeek}</DateNoteWeekday>
                                                <DateNoteDay>{lessonBlockData?.date?.dayNumber}</DateNoteDay>
                                                <DateNoteMonth>{lessonBlockData?.date?.month}</DateNoteMonth>
                                            </DateNote>}
                                            <DescriptionNoteDetails active={editableLesson === lesson?.id}>
                                                 <FlexBox direction="column" directionMobile="column">
                                                     {lesson?.type == LMSLessonType.WithDate && <DescriptionNoteTime>{lessonBlockData?.time}</DescriptionNoteTime>}
                                                    <DescriptionNoteTheme>{lessonBlockData?.themeName}</DescriptionNoteTheme>
                                                     {lesson?.type !== LMSLessonType.WithoutDate && <DescriptionNoteLink onClick={() => window.open(lesson?.lessonLink, '_blank')}>{lesson?.type === LMSLessonType.Video ? 'Посмотреть видео' : 'Посмотреть занятие'}</DescriptionNoteLink>}
                                                    <DescriptionNoteText>Группа {lessonBlockData?.groupsName}</DescriptionNoteText>
                                                </FlexBox>
                                                <DescriptionNoteGroupButtons>
                                                    <DescriptionNoteEditButton onClick={() => onStartEdit(lesson?.id)}>
                                                        <CustomEditIcon iconSize="16px" />
                                                        </DescriptionNoteEditButton>
                                                        <DescriptionNoteDeleteButton onClick={() => onDelete(lesson?.id)}>
                                                            <CustomDeleteIcon/>
                                                        </DescriptionNoteDeleteButton>
                                                </DescriptionNoteGroupButtons>
                                            </DescriptionNoteDetails>
                                        </DescriptionNote></SortableItem>
                                    })}
                                </DescriptionBlockNote>
                                </SortableList>
                            </ScheduleModalNote>
                        </ScheduleModalNotes>
                    </ScheduleModalPlanner>

                    <ScheduleModalСalendar>
                        <CalendarComponent setNewDate={setNewLessonDate} newDate={newLessonDate} eventsDate={eventsDate} />
                    </ScheduleModalСalendar>
                    </ScheduleModalLessonsContent>
                </ScheduleModalContent>
            </ScheduleModalWrapper>
        </ScheduleModalBody>
    );
};
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {FlexBox} from '@brg/ui';
import {
    CustomDeleteIcon,
    CustomEditIcon,
    DateNote,
    DateNoteDay,
    DateNoteMonth,
    DateNoteWeekday,
    DescriptionBlockNote,
    DescriptionNote,
    DescriptionNoteDeleteButton,
    DescriptionNoteDetails,
    DescriptionNoteEditButton,
    DescriptionNoteGroupButtons,
    DescriptionNoteLink,
    DescriptionNoteText,
    DescriptionNoteTheme,
    DescriptionNoteTime,
    InputSectionFlexBox,
    LMSScheduleEditPageGridInput,
    ScheduleButtonWrapper,
    ScheduleEditPageGridLabel,
    ScheduleModalAddButton,
    ScheduleModalBody,
    ScheduleModalCancelButton,
    ScheduleModalContent,
    ScheduleModalForm,
    ScheduleModalInput,
    ScheduleModalLessonsContent,
    ScheduleModalNote,
    ScheduleModalNotes,
    ScheduleModalPlanner,
    ScheduleModalTimeInput,
    ScheduleModalTitle,
    ScheduleModalWrapper,
    ScheduleModalСalendar
} from './styles';
import {InputSection} from '@brg/controls';
import {Input} from "@brg/input";
import {CalendarComponent} from './CalendarComponent/CalendarComponent';
import {ILesson, IThemeItem, LMSLessonType} from "../../constants/ILMS";
import {useTeamGroups} from "../../hooks/useTeamGroups";
import {useGetThemeLessons} from "../../hooks/api/LMS/useGetThemeLessons";
import {API, Endpoints, Group} from "@brg/api";
import {useTranslation} from "react-i18next";
import {makeBilingualLangObject} from "../../helpers/changeLangObject";
import {DottedIcon} from "../../icons/DottedIcon";
import SortableList, {SortableItem} from "react-easy-sort";
import { arrayMoveImmutable } from 'shared/utils/arrayMove';

const fixTimezoneOffsetToShow = (date: Date | undefined) : Date => {
    if(!date) return new Date();
    return new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
}

const fixTimezoneOffsetToSave = (date: Date | undefined) : Date => {
    if(!date) return new Date();
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
}

export interface ScheduleModalProps {
    themes: IThemeItem<'Local'>[];
    currentTeamId?: string;
    projectId?: string;
    themeId?: string;
}

export const LESSON_OPTIONS = [
    {label: "Видео", value: LMSLessonType.Video},
    {label: "По дате", value: LMSLessonType.WithDate},
    {label: "Без даты", value: LMSLessonType.WithoutDate},
]

export const ScheduleModal: FC<ScheduleModalProps> = (props) => {

    const {
        themeId,
        currentTeamId,
        projectId,
        themes
    } = props;

    const [t, setT] = useState(Date.now())
    const [tLessons, setTLessons] = useState(Date.now())
    const groups = useTeamGroups(currentTeamId || "", t)
    const { i18n } = useTranslation()
    const translatedGroups = useMemo(() => API.toLang(groups, i18n.language) as Group<'Local'>[], [i18n.language, groups])
    const [activeTheme, setActiveTheme] = useState<null | {value: string, label: string}>(null)
    const {lessons: themeLessons} = useGetThemeLessons(projectId, activeTheme?.value, tLessons)
    const [lessons, setLessons] = useState<ILesson<'Global'>[]>([]);

    useEffect(() => {
        const grouppedLessons = [...themeLessons].map((i) => {
            return {
                ...i,
            }
        })
        setLessons(grouppedLessons)
    }, [themeLessons])

    const themeOptions = useMemo(() => themes?.map((i) => ({value: i?.id || "", label: i?.name || ""})) || [], [themes])
    const [editableLesson, setEditableLesson] = useState<null | string>(null)
    const [startTime, setStartTime] = useState('00:00')
    const [endTime, setEndTime] = useState('00:00')
    const [link, setLink] = useState('')
    const [name, setName] = useState([{lang: 'ru', name: ''}, {lang: 'en', name: ''}])
    const translatedName = useMemo(() => (API.toLang({val: name}, i18n.language) as any)?.val as string, [name, i18n])
    const [lessonType, setLessonType] = useState<{value: LMSLessonType, label: string}>(LESSON_OPTIONS[1])

    useEffect(() => {
        setActiveTheme(themeOptions?.find(i => i?.value === themeId) || null)
    }, [themeOptions])

    // @ts-ignore
    const groupOptions = useMemo(() => translatedGroups?.map((i) => ({value: i?.id || "", label: i?.title || ""})) || [], [translatedGroups])
    const [activeGroups, setActiveGroups] = useState<{value: string, label: string}[]>([])
    const [newLessonDate, setNewLessonDate] = useState<Date | undefined>(new Date())
    const eventsDate = useMemo(() => {
        if (!themeLessons || themeLessons?.length === 0) return [];
        return themeLessons?.map((lesson: ILesson) => fixTimezoneOffsetToShow(new Date(lesson.start)));
    }, [themeLessons])

    // @ts-ignore
    const onChangeTime = useCallback((value, setValue) => {
        const values = value.split(':');

        if (values?.length < 2) return;

        setValue(`${values[0] > 23 ? 23 : values[0]}:${values[1] > 59 ? 59 : values[1]}`)
    }, [])

    const isConfirmButtonActive = useMemo(() => {

        const firstTimeArr = startTime?.split(':')
        const firstTime = Number(firstTimeArr[0]) * 60 + Number(firstTimeArr[1])
        const secondTimeArr = endTime?.split(':')
        const secondTime = Number(secondTimeArr[0]) * 60 + Number(secondTimeArr[1])

        if (lessonType?.value === LMSLessonType.WithDate) return link?.length > 0 && newLessonDate && activeTheme && activeGroups?.length > 0 && firstTime < secondTime
        if (lessonType?.value === LMSLessonType.Video) return link?.length > 0 && activeGroups?.length > 0
        return activeGroups?.length > 0

    }, [link, newLessonDate, activeTheme, activeGroups, startTime, endTime, lessonType])

    const getLessonBlockData = useCallback((lesson: ILesson) => {

        const startDate = new Date(lesson?.start);
        const endDate = new Date(lesson?.end);
        const pad = (n: any) => n < 10 ? '0' + n : n;

        const options = { weekday: 'short', day: 'numeric', month: 'long' };
        // @ts-ignore
        const formattedDate = startDate.toLocaleDateString('ru-RU', options)?.split(' ');
        const startHours = pad(startDate.getHours());
        const startMinutes = pad(startDate.getMinutes());
        const endHours = pad(endDate.getHours());
        const endMinutes = pad(endDate.getMinutes());
        const neededGroups = translatedGroups?.filter(i => lesson?.groupIds?.includes(i?.id));
        const groupsName = neededGroups
            // @ts-ignore
            ?.reduce((acc, curr, currentIndex) => (`${acc} ${curr?.title}${currentIndex + 1 === neededGroups?.length ? '' : ','}`), '')

        // @ts-ignore
        const title = API.toLang({val: lesson?.title}, 'ru')?.val?.length > 0 ? API.toLang({val: lesson?.title}, 'ru')?.val : ''

        return {
            themeName: title,
            date: {
                dayWeek: (formattedDate[0] || '')?.toUpperCase()?.slice(0, -1),
                dayNumber: formattedDate[1] || '',
                month: formattedDate[2] || '',
            },
            groupsName: groupsName,
            time: `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`
        }
    }, [themes, translatedGroups])

    const onDelete = useCallback((id: string) => {

        const confirmation = window.confirm('Вы действительно хотите удалить занятие?');

        if (!confirmation) return;

        if (editableLesson === id) setEditableLesson(null);

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(`/api/LMS/DeleteThemeLesson`)
            .post()
            .body({
                id,
                themeId: activeTheme?.value,
                moduleProjectId: projectId
            })
            .release()
            .then((r) => {
                setTLessons(Date.now())
            })
    }, [activeTheme, projectId, editableLesson])

    const onReset = useCallback(() => {
        setStartTime('00:00')
        setEndTime('00:00')
        setLink('')
        setActiveGroups([])
        setName(makeBilingualLangObject(''))
        setLessonType(LESSON_OPTIONS[1])
    }, [])

    const onStartEdit = useCallback((id: string) => {

        if (editableLesson === id) {
            setEditableLesson(null)
            onReset()
            return;
        }
        const lesson = lessons?.find(i => i?.id === id);

        if (!lesson) return;

        const startDate = new Date(lesson?.start);
        const endDate = new Date(lesson?.end);
        const pad = (n: any) => n < 10 ? '0' + n : n;

        const startHours = pad(startDate.getHours());
        const startMinutes = pad(startDate.getMinutes());
        const endHours = pad(endDate.getHours());
        const endMinutes = pad(endDate.getMinutes());

        startDate.setHours(0)
        startDate.setMinutes(0)

        setStartTime(`${startHours}:${startMinutes}`)
        setEndTime(`${endHours}:${endMinutes}`)
        setLink(lesson?.lessonLink)
        setName(lesson?.title)
        setLessonType(LESSON_OPTIONS[lesson?.type === undefined ? 1 : lesson?.type])
        setNewLessonDate(startDate)
        setActiveGroups(groupOptions.filter(i => lesson?.groupIds?.includes(i?.value)))

        setEditableLesson(id)
    }, [lessons, groupOptions, editableLesson])

    const onCancel = useCallback(() => {
        setEditableLesson(null)
        onReset()
    }, [])

    const onSort = useCallback((oldIndex: number, newIndex: number) => {
        const arr = arrayMoveImmutable(lessons, oldIndex, newIndex)
        setLessons(arr)

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(`/api/LMS/UpdateThemeLessonPositions`)
            .post()
            .body(arr?.map(i => i?.id || "") || [])
            .release()
            .then((r) => r.json)

    }, [lessons])

    const onConfirm = useCallback(() => {

        const firstTimeArr = startTime?.split(':')
        const secondTimeArr = endTime?.split(':')

        const startDate = fixTimezoneOffsetToSave(new Date(newLessonDate || Date.now()))
        startDate.setHours(Number(firstTimeArr[0]));
        startDate.setMinutes(Number(firstTimeArr[1]));
        const endDate = fixTimezoneOffsetToSave(new Date(newLessonDate || Date.now()))
        endDate.setHours(Number(secondTimeArr[0]));
        endDate.setMinutes(Number(secondTimeArr[1]));

        const lesson = lessons?.find(i => i?.id === editableLesson);
        const firstBody = editableLesson ? lesson : {}

        const body = {
            ...firstBody,
            moduleProjectId: projectId,
            themeId: activeTheme?.value,
            start: startDate,
            end: endDate,
            groupIds: activeGroups?.map(i => i?.value) || [],
            lessonLink: link,
            lessonLinkText: [{lang: "ru", name: ""},{lang: "en", name: ""}],
            title: name,
            type: lessonType?.value,
        }

        onReset();
        setEditableLesson(null)

        API.request()
            .endpoint(Endpoints.geoGateway)
            .url(editableLesson ? `/api/LMS/UpdateThemeLesson` : `/api/LMS/AddThemeLesson`)
            .post()
            .body(body)
            .release()
            .then((r) => r.json)
            .then((r) => {
                setTLessons(Date.now())
            })

    }, [projectId, activeTheme, activeGroups, link, startTime, endTime, newLessonDate, editableLesson, lessons, name, lessonType])

    return (
        <ScheduleModalBody offsetTop={70} width={'100%'} justify='center' align='center' justify-content='center'>
            <ScheduleModalWrapper>
                <ScheduleModalTitle>Расписание</ScheduleModalTitle>
                <ScheduleModalContent>
                    <ScheduleModalForm>
                        <InputSection>
                            <InputSectionFlexBox>
                            <FlexBox direction="column" directionMobile="column" width="100%">
                                <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                        {'Тема'}
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Dropdown
                                            value={activeTheme}
                                            onChange={setActiveTheme}
                                            options={themeOptions}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>

                                <FlexBox width="100%" justify="space-between">
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                            {'Тип урока'}
                                        </ScheduleEditPageGridLabel>
                                        <LMSScheduleEditPageGridInput>
                                            <Input.Dropdown
                                                value={lessonType}
                                                onChange={setLessonType}
                                                options={LESSON_OPTIONS}
                                            />
                                        </LMSScheduleEditPageGridInput>
                                    </ScheduleModalInput>
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }} gx={8}>
                                            <span>{'Название'}</span>
                                        </ScheduleEditPageGridLabel>
                                        <LMSScheduleEditPageGridInput>
                                            <Input.Text
                                                className={'text-input'}
                                                placeholder={'Введите название урока'}
                                                value={translatedName}
                                                onChange={(e: any) => setName(makeBilingualLangObject(e.target.value))}
                                            />
                                        </LMSScheduleEditPageGridInput>
                                    </ScheduleModalInput>
                                </FlexBox>

                                {lessonType?.value == LMSLessonType.WithDate && <>
                                    <ScheduleModalInput>
                                        <ScheduleEditPageGridLabel>{'Начало занятия'}</ScheduleEditPageGridLabel>
                                        <ScheduleModalTimeInput directionMobile={"column"} gyMobile={8}>
                                            <input className={'time-input'} type={'time'} value={startTime} onChange={(e: any) => onChangeTime(e.target.value, setStartTime)} />
                                        </ScheduleModalTimeInput>
                                    </ScheduleModalInput>

                                    <ScheduleModalInput>
                                        <ScheduleModalTimeInput directionMobile={"column"} gyMobile={8}>
                                            <ScheduleEditPageGridLabel>{'Окончание занятия'}</ScheduleEditPageGridLabel>
                                            <input className={'time-input'} type={'time'} value={endTime} onChange={(e: any) => onChangeTime(e.target.value, setEndTime)} />
                                        </ScheduleModalTimeInput>
                                    </ScheduleModalInput>
                                </>}

                            </FlexBox>
                            <FlexBox direction="column" directionMobile="column" width="100%">
                                {lessonType?.value !== LMSLessonType.WithoutDate && <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }} gx={8}>
                                        <span>{lessonType?.value == LMSLessonType.Video ? 'Ссылка на видео' : 'Ссылка на занятие'}</span>
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Text
                                            className={'text-input'}
                                            placeholder={lessonType?.value == LMSLessonType.Video ? 'Вставьте ссылку на видео' : 'Вставьте ссылку на занятие'}
                                            value={link}
                                            onChange={(e: any) => setLink(e.target.value)}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>}

                                <ScheduleModalInput>
                                    <ScheduleEditPageGridLabel offsetBottom={16} offsetBottomMobile={16} style={{ paddingTop: 0, width: '100%' }}>
                                        {'Группа'}
                                    </ScheduleEditPageGridLabel>
                                    <LMSScheduleEditPageGridInput>
                                        <Input.Dropdown
                                            isMulti
                                            value={activeGroups}
                                            onChange={setActiveGroups}
                                            options={groupOptions}
                                        />
                                    </LMSScheduleEditPageGridInput>
                                </ScheduleModalInput>
                            </FlexBox>
                            </InputSectionFlexBox>
                        </InputSection>
                        <ScheduleButtonWrapper width="100%" direction="column" directionMobile="column">
                            <ScheduleModalAddButton
                                className="add-button"
                                disabled={!isConfirmButtonActive}
                                onClick={onConfirm}
                            >
                                {editableLesson ? 'Сохранить изменения' : 'Добавить занятие'}
                            </ScheduleModalAddButton>
                            {editableLesson && <ScheduleModalCancelButton
                                className="add-button"
                                onClick={onCancel}
                            >
                                {'Отменить'}
                            </ScheduleModalCancelButton>}
                        </ScheduleButtonWrapper>
                    </ScheduleModalForm>
                    <ScheduleModalLessonsContent>
                    <ScheduleModalPlanner>
                        <ScheduleModalNotes>
                            <ScheduleModalNote>
                                <SortableList onSortEnd={onSort}>
                                <DescriptionBlockNote>
                                    {lessons?.map(lesson => {

                                        const lessonBlockData = getLessonBlockData(lesson);

                                        return <SortableItem key={"team-tab-sortable_" + lesson?.id}><DescriptionNote key={lesson?.id}>
                                            <FlexBox width={24}>
                                                <DottedIcon/>
                                            </FlexBox>
                                            {lesson?.type == LMSLessonType.WithDate && <DateNote isFirst={true}>
                                                <DateNoteWeekday>{lessonBlockData?.date?.dayWeek}</DateNoteWeekday>
                                                <DateNoteDay>{lessonBlockData?.date?.dayNumber}</DateNoteDay>
                                                <DateNoteMonth>{lessonBlockData?.date?.month}</DateNoteMonth>
                                            </DateNote>}
                                            <DescriptionNoteDetails active={editableLesson === lesson?.id}>
                                                 <FlexBox direction="column" directionMobile="column">
                                                     {lesson?.type == LMSLessonType.WithDate && <DescriptionNoteTime>{lessonBlockData?.time}</DescriptionNoteTime>}
                                                    <DescriptionNoteTheme>{lessonBlockData?.themeName}</DescriptionNoteTheme>
                                                     {lesson?.type !== LMSLessonType.WithoutDate && <DescriptionNoteLink onClick={() => window.open(lesson?.lessonLink, '_blank')}>{lesson?.type === LMSLessonType.Video ? 'Посмотреть видео' : 'Посмотреть занятие'}</DescriptionNoteLink>}
                                                    <DescriptionNoteText>Группа {lessonBlockData?.groupsName}</DescriptionNoteText>
                                                </FlexBox>
                                                <DescriptionNoteGroupButtons>
                                                    <DescriptionNoteEditButton onClick={() => onStartEdit(lesson?.id)}>
                                                        <CustomEditIcon iconSize="16px" />
                                                        </DescriptionNoteEditButton>
                                                        <DescriptionNoteDeleteButton onClick={() => onDelete(lesson?.id)}>
                                                            <CustomDeleteIcon/>
                                                        </DescriptionNoteDeleteButton>
                                                </DescriptionNoteGroupButtons>
                                            </DescriptionNoteDetails>
                                        </DescriptionNote></SortableItem>
                                    })}
                                </DescriptionBlockNote>
                                </SortableList>
                            </ScheduleModalNote>
                        </ScheduleModalNotes>
                    </ScheduleModalPlanner>

                    <ScheduleModalСalendar>
                        <CalendarComponent setNewDate={setNewLessonDate} newDate={newLessonDate} eventsDate={eventsDate} />
                    </ScheduleModalСalendar>
                    </ScheduleModalLessonsContent>
                </ScheduleModalContent>
            </ScheduleModalWrapper>
        </ScheduleModalBody>
    );
};
